import React, { useState } from "react";
import { motion } from "framer-motion";
import "./EmailSubscription.css";

const EmailSubscription: React.FC = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");

  // Google Apps Script Web App URL
  const GOOGLE_SCRIPT_URL =
    process.env.REACT_APP_GOOGLE_SCRIPT_URL ||
    "https://script.google.com/macros/s/AKfycby_2cwnI3FRLpgJZVizy8qZH-cgpeHCnDHuuMa3LLKcuaT6pEOGmF9DfUjNoF0MSMpLXg/exec";

  // Email validation function
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email.trim());
  };

  // Validate email on input change
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);

    // Clear previous errors when user starts typing
    if (error) {
      setError("");
    }
  };

  // Timeout wrapper for fetch requests
  const fetchWithTimeout = (
    url: string,
    options: RequestInit,
    timeout = 8000
  ): Promise<Response> => {
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error("Request timeout"));
      }, timeout);

      fetch(url, options)
        .then((response) => {
          clearTimeout(timeoutId);
          resolve(response);
        })
        .catch((err) => {
          clearTimeout(timeoutId);
          reject(err);
        });
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Trim whitespace
    const trimmedEmail = email.trim();

    // Validate email
    if (!trimmedEmail) {
      setError("Please enter your email address.");
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setError("Please enter a valid email address.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      // Submit to Google Sheets via Apps Script with timeout and optimized settings
      const response = await fetchWithTimeout(
        GOOGLE_SCRIPT_URL,
        {
          method: "POST",
          mode: "no-cors", // Avoids CORS preflight requests for faster connection
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams({
            email: trimmedEmail,
            timestamp: new Date().toISOString(),
            source: "email_subscription",
          }).toString(),
        },
        6000
      ); // 6 second timeout

      // With no-cors mode, we can't read response status, so we assume success
      setIsSubmitted(true);
      setEmail(""); // Clear email after successful submission
    } catch (error) {
      console.error("Error submitting email:", error);

      // Handle different types of errors
      if (error instanceof Error) {
        if (
          error.message.includes("timeout") ||
          error.message.includes("Request timeout")
        ) {
          // For timeout, still show success since the request might have succeeded
          setIsSubmitted(true);
          setEmail("");
        } else if (error.message.includes("fetch")) {
          setError(
            "Connection issue. Please check your internet and try again."
          );
        } else {
          setError("Submission error. Please try again.");
        }
      } else {
        setError("Unexpected error. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div id="email-subscription" className="email-subscription-section">
      <div className="email-subscription-container">
        {!isSubmitted ? (
          <motion.div
            className="email-subscription-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* Header */}
            <div className="email-header">
              <h2>Be the first to play Seekers: Shards of Imriel</h2>
              <h3>Sign up for exclusive previews, alpha playtesting, and kickstarter launch updates</h3>
            </div>

            {/* Form */}
            <form className="email-form" onSubmit={handleSubmit}>
              <div className="form-group email-form-group">
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={handleEmailChange}
                  placeholder="Enter your email address"
                  required
                  disabled={isSubmitting}
                />
                {error && (
                  <motion.div
                    className="error-message"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {error}
                  </motion.div>
                )}
              </div>

              <motion.button
                type="submit"
                className="submit-btn1"
                disabled={isSubmitting || !email}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isSubmitting ? (
                  <span className="loading-spinner">
                    <div className="spinner"></div>
                    Joining...
                  </span>
                ) : (
                  "Sign Up"
                )}
              </motion.button>
            </form>
          </motion.div>
        ) : (
          /* Success State */
          <motion.div
            className="email-success"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", damping: 10 }}
          >
            <motion.div
              className="success-icon"
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", damping: 10 }}
            >
              ✓
            </motion.div>
            <h2>You are now signed up!</h2>
            <p>
              Thank you for joining our community! Follow our <a href="https://www.kickstarter.com/projects/1204577492/seekers-shards-of-imriel" target="_blank" rel="noopener noreferrer">Kickstarter</a> page to receive exclusive updates and rewards when we launch.
                      </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default EmailSubscription;
